/**
 * useNotifications — React binding for the `notificationStore` singleton.
 *
 * Uses useSyncExternalStore so the bell re-renders whenever the store changes,
 * from anywhere in the app (socket handlers, API layer, etc.).
 *
 * Pass `cityFilter` (the active projectId) to scope the feed to one city.
 * Notifications tagged with that projectId — plus any untagged ("all cities")
 * notifications — are shown; everything else is hidden. The returned
 * markAllRead/clearAll act only on the visible (city-scoped) notifications.
 */

import { useSyncExternalStore, useMemo, useCallback } from 'react';
import notificationStore from '../services/notificationStore';

const subscribe = (cb) => notificationStore.subscribe(cb);
const getSnapshot = () => notificationStore.getAll();

export function useNotifications(cityFilter = null) {
  const all = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const notifications = useMemo(() => {
    if (!cityFilter) return all;
    return all.filter((n) => !n.projectId || n.projectId === cityFilter);
  }, [all, cityFilter]);

  const unreadCount = useMemo(
    () => notifications.reduce((n, it) => (it.read ? n : n + 1), 0),
    [notifications]
  );

  const markAllRead = useCallback(() => {
    const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    if (unreadIds.length) notificationStore.markAllRead(unreadIds);
  }, [notifications]);

  const clearAll = useCallback(() => {
    notificationStore.clearAll(notifications.map((n) => n.id));
  }, [notifications]);

  return {
    notifications,
    unreadCount,
    markRead: (id) => notificationStore.markRead(id),
    markAllRead,
    remove: (id) => notificationStore.remove(id),
    clearAll,
  };
}

export default useNotifications;
